import React, { useRef, useState } from "react"
import { omit, pick } from "lodash"

import { Element, ElementProps } from "legacy/components/shared/base"
import StyledInput, { StyledInputProps } from "./StyledInput"
import { useCombinedRef, useTheme } from "src/legacy/utils"

export type InputProps = Omit<StyledInputProps, "theme" | "height" | "width"> &
  Pick<ElementProps, "height" | "width">

const STYLED_INPUT_WRAPPER_PROP_LIST = [
  "height",
  "maxWidth",
  "padding",
  "position",
  "width",
]

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ onBlur, onFocus, ...props }, forwardedRef): React.ReactElement => {
    const [isFocused, setIsFocused] = useState(false)
    const inputProps = omit(
      props,
      STYLED_INPUT_WRAPPER_PROP_LIST
    ) as StyledInputProps
    const inputWrapperProps = pick(props, STYLED_INPUT_WRAPPER_PROP_LIST)

    const { zIndices } = useTheme()

    const ref = useCombinedRef<HTMLInputElement>(
      forwardedRef,
      useRef<HTMLInputElement>(null)
    )

    const _onBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
      setIsFocused(false)
      if (onBlur) onBlur(e)
    }

    const _onClick = (): void => {
      ref?.current?.focus()
      ref?.current?.click()
    }

    const _onFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
      setIsFocused(true)
      if (onFocus) onFocus(e)
    }

    const neutralBorder =
      inputProps.variant === "primary" ? "input.primary" : "neutral"

    return (
      <Element
        borderBottom={isFocused ? "focus" : neutralBorder}
        borderLeft={isFocused ? "focus" : "neutral"}
        borderRight={isFocused ? "focus" : "neutral"}
        borderTop={isFocused ? "focus" : "neutral"}
        borderRadius={isFocused ? "focus" : "none"}
        boxShadow={isFocused ? "focus" : "neutral"}
        display="inline-block"
        padding="1px"
        zIndex={zIndices.focus}
        {...inputWrapperProps}
      >
        <StyledInput
          onBlur={_onBlur}
          onClick={_onClick}
          onFocus={_onFocus}
          ref={ref}
          {...inputProps}
        />
      </Element>
    )
  }
)

export default Input
